import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
    Box,
    Button,
    Grid,
    IconButton,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import imageBackground from 'assets/images/backgrounds/bg-start-survey.jpeg';
import CloseIcon from '@mui/icons-material/Close';
import SubdirectoryArrowLeftIcon from '@mui/icons-material/SubdirectoryArrowLeft';
import BrandLogo from 'common/components/brand-logo';
import React, { useEffect, useState } from 'react';
import { createStyleSheet, handleError } from 'utils/helpers';
import ModalShareSurvey from '../components/modal-share-survey';
import ModalConfirm from 'common/components/modal-confirm';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { previewLogicPageQuestionFormatter } from 'utils/preview_helpers';
import { fetchLogicPageSurveyInfo } from 'services/previewCampaign.service';
import { useAppSelector, useAppDispatch } from 'common/hook';
import {
    setMe,
    setSurveyState,
    setListQuestion,
    resetCaptureTimeStamp,
    captureTimeStamp,
} from 'slices/previewLogicPageSurveySlice';
import { setAnonymousGroup } from 'slices/userSlice';
import { ICampaignQuestionDetail } from 'types/campaign';
import { IGroup } from 'types/user';
import { toast } from 'react-toastify';
import { joinSurvey, fetchSurveyGroup } from 'services/campaign.service';
import { IPreviewSurveyState } from 'types/previewCampaign';
import { setGeoLocation, resetGeoLocation } from 'slices/geoLocationSlice';
import useGeolocation from 'common/hooks/use-geolocation';
import RequireLocationGate from 'common/components/require-location-gate';

interface IStartSurveyProps {
    setSurveyStep: (step: number) => void;
}
const StartSurvey: React.FC<IStartSurveyProps> = ({ setSurveyStep }) => {
    const [openModalShareSurvey, setOpenModalShareSurvey] = useState(false);
    const [openModalCampaignStatus, setOpenModalCampaignStatus] =
        React.useState(false);
    const [openStaffIdModal, setOpenStaffIdModal] = useState(false);
    const [bgImage, setBgImage] = useState<string>(imageBackground);
    const [bgReady, setBgReady] = useState(false);
    const [welcomeTextColor, setWelcomeTextColor] = useState('#FFFFFF');
    const campaignId = useParams<{ id: string }>().id;
    const me = useAppSelector((state) => state.previewLogicPageSurvey.me);
    const questions = useAppSelector(
        (state) => state.previewLogicPageSurvey.question_pages
    );
    const surveyState = useAppSelector(
        (state) => state.previewLogicPageSurvey.surveyState
    );
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const surveyCode = query.get('surveyCode');
    const uniqCode = query.get('code');
    const isSecretSurvey = query.get('isSecretSurvey');
    const queryStaffId = query.get('staffId') || query.get('staff_id') || '';
    const [staffId, setStaffId] = useState(queryStaffId);
    const {
        status: geoStatus,
        coords: geoCoords,
        error: geoError,
        requestLocation,
    } = useGeolocation();

    // Chỉ bắt bật vị trí khi campaign bật cờ is_required_location VÀ khảo sát đã
    // sẵn sàng trả lời (đã qua cổng nhập mã, đã tải xong câu hỏi).
    const surveyReady = !!surveyState.answerable && questions.length > 0;
    const requireLocation = surveyReady && !!surveyState.is_required_location;
    const locationReady = !requireLocation || geoStatus === 'granted';

    useEffect(() => {
        dispatch(resetGeoLocation());
    }, [dispatch]);

    useEffect(() => {
        if (requireLocation) {
            requestLocation();
        }
    }, [requireLocation, requestLocation]);

    useEffect(() => {
        if (geoCoords) {
            dispatch(setGeoLocation(geoCoords));
        }
    }, [geoCoords, dispatch]);

    // Chưa cấp quyền vị trí thì không cho vào phần trả lời, kể cả bằng phím Enter.
    const handleStartSurvey = () => {
        if (!locationReady) return;
        dispatch(captureTimeStamp());
        setSurveyStep(1);
    };

    // Apply theme + preload ảnh nền. Resolve sau khi ảnh sẵn sàng (hoặc ngay nếu không có ảnh)
    // để chỗ gọi chờ được rồi mới bật bgReady -> paint đầu tiên là brand, tránh nháy.
    const applyGroupTheme = (group?: IGroup): Promise<void> => {
        if (!group) return Promise.resolve();
        dispatch(setAnonymousGroup(group));
        setWelcomeTextColor(
            group.theme_color?.survey?.welcome?.textColor || '#FFFFFF'
        );
        const apiImage = group.survey_image;
        if (!apiImage) return Promise.resolve();
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                setBgImage(apiImage);
                resolve();
            };
            img.onerror = () => resolve();
            img.src = apiImage;
        });
    };

    // Màn nhập mã bảo mật chưa gọi API nào -> tải group riêng để có theme sớm.
    const loadThemeForGate = async () => {
        try {
            const res = await fetchSurveyGroup(campaignId ?? '');
            if (res.success) await applyGroupTheme(res.data.groups?.[0]);
        } catch {
            // không có theme -> giữ fallback mặc định
        }
        setBgReady(true);
    };

    const getPreviewToken = async (
        surveyCode: string,
        uniqCode: string | null,
        staffIdValue?: string
    ) => {
        try {
            if (!staffIdValue && !!queryStaffId) {
                staffIdValue = queryStaffId;
            }
            const payload = {
                surveyCode,
                code: uniqCode || undefined,
                ...(staffIdValue && { staff_id: staffIdValue }),
            };
            const response = await joinSurvey(
                payload.surveyCode,
                payload.code,
                payload.staff_id
            );
            if (response.success) {
                applyGroupTheme(response.data?.groups?.[0]);
                const newToken = response.data.token;
                const codeFromResponse = response.data.code;
                const staffIdFromResponse = response.data.staff_id;
                const queryParams = new URLSearchParams();
                queryParams.set('token', newToken);
                if (codeFromResponse) {
                    queryParams.set('code', codeFromResponse);
                }
                if (staffIdFromResponse) {
                    queryParams.set('staff_id', staffIdFromResponse);
                }
                navigate(
                    `/survey/page-logic/${campaignId}?${queryParams.toString()}`,
                    { replace: true }
                );
                return null;
            } else {
                // Hết hạn/invalid: chờ theme + ảnh nền xong rồi mới hiện -> tránh nháy bg.
                await applyGroupTheme(response.data?.groups?.[0]);
                toast.error(response.message);
                setBgReady(true);
                return response.data.error_code;
            }
        } catch (error) {
            handleError(error);
            setBgReady(true);
            return null;
        }
    };

    const handleStaffIdSubmit = async () => {
        if (surveyCode && surveyCode.length > 0) {
            const errorCode = await getPreviewToken(
                surveyCode,
                isSecretSurvey === 'true' ? staffId : uniqCode,
                staffId
            );
            if (
                !errorCode ||
                (errorCode !== 'STAFF_ID_REQUIRED' &&
                    errorCode !== 'STAFF_ID_NOT_MATCH' &&
                    errorCode !== 'CODE_INVALID')
            ) {
                setOpenStaffIdModal(false);
            }
            setStaffId('');
        }
    };

    useEffect(() => {
        const fetchInfo = async () => {
            const fetchData = await fetchLogicPageSurveyInfo(campaignId ?? '');
            if (fetchData.success) {
                const group = fetchData.data.groups?.[0];
                if (group) {
                    dispatch(setAnonymousGroup(group));
                }
                setWelcomeTextColor(
                    group?.theme_color?.survey?.welcome?.textColor || '#FFFFFF'
                );
                dispatch(setMe(fetchData.data.me));
                dispatch(
                    setSurveyState({
                        ...fetchData.data.survey.attributes,
                        id: fetchData.data.survey.id,
                    })
                );
                dispatch(setSurveyState({ answerable: true }));
                const formattedData = (
                    (fetchData.data.question_pages as []) || []
                ).map(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (page: any) => {
                        const formattedQuestions = (page.questions || []).map(
                            (q: ICampaignQuestionDetail) =>
                                previewLogicPageQuestionFormatter(q)
                        );
                        return {
                            id: page.id,
                            page_number: page.page_number,
                            title: page.title,
                            questions: formattedQuestions,
                        };
                    }
                );
                dispatch(setListQuestion(formattedData));

                const apiImage = group?.survey_image;
                const targetUrl = apiImage || imageBackground;
                const img = new Image();
                img.onload = () => {
                    setBgImage(targetUrl);
                    setBgReady(true);
                };
                img.onerror = () => {
                    setBgImage(imageBackground);
                    setBgReady(true);
                };
                img.src = targetUrl;
            } else {
                if (fetchData.status_code === 2100) {
                    dispatch(
                        setSurveyState({
                            ...fetchData.data.survey.attributes,
                            id: fetchData.data.survey.id,
                        })
                    );
                    setOpenModalCampaignStatus(true);
                } else {
                    toast.error(fetchData.message);
                }
                setBgReady(true);
            }
        };

        dispatch(resetCaptureTimeStamp());
        if (!!surveyCode && surveyCode.length > 0) {
            if (
                ((!!uniqCode && uniqCode.length > 0) ||
                    isSecretSurvey === 'true') &&
                !queryStaffId
            ) {
                setOpenStaffIdModal(true);
                loadThemeForGate();
            } else {
                getPreviewToken(surveyCode, uniqCode, staffId);
            }
        } else {
            fetchInfo();
        }
    }, [
        campaignId,
        dispatch,
        surveyCode,
        uniqCode,
        isSecretSurvey,
        queryStaffId,
    ]);

    // eslint-disable-next-line
    const handleEnterKey = async (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleStartSurvey();
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleEnterKey);
        return () => {
            window.removeEventListener('keydown', handleEnterKey);
        };
    }, [handleEnterKey]);

    const closeSurvey = (campaignId: number, me: IPreviewSurveyState['me']) => {
        let urlToBack = '';
        if (me.creator) {
            urlToBack = `/admin/campaign/design/page-logic/${campaignId}`;
        } else if (me.type === 'Admin') {
            urlToBack = `/admin/dashboard`;
        }
        navigate(urlToBack);
    };

    if (!bgReady) {
        return (
            <Box
                sx={{
                    width: '100vw',
                    height: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <RequireLocationGate
            status={geoStatus}
            error={geoError}
            onRequest={requestLocation}
            enabled={requireLocation}
        >
            <Box
                sx={{
                    ...styles.startSurveyLayout,
                    backgroundImage: `url(${bgImage})`,
                }}
            >
                <Grid
                    container
                    spacing={2}
                    sx={{
                        marginBottom: ['3rem', '4rem', '6rem', '8rem'],
                        alignItems: ['flex-end'],
                    }}
                >
                    <Grid item xs={12} md={4} sm={6}>
                        <BrandLogo />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={8}
                        sx={{
                            display: ['none', 'flex', 'flex', 'flex'],
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        {me.creator && (
                            <IconButton
                                onClick={() =>
                                    closeSurvey(Number(surveyState.id), me)
                                }
                                className="close-surveys"
                            >
                                <CloseIcon
                                    sx={{
                                        color: (theme) =>
                                            theme.palette.common.white,
                                        width: [
                                            '2.5rem',
                                            '2rem',
                                            '2rem',
                                            '3rem',
                                            '3rem',
                                        ],
                                        height: [
                                            '2.5rem',
                                            '2rem',
                                            '2rem',
                                            '3rem',
                                            '3rem',
                                        ],
                                    }}
                                />
                            </IconButton>
                        )}
                    </Grid>
                </Grid>

                <Grid
                    container
                    spacing={2}
                    sx={{
                        marginBottom: ['3rem'],
                    }}
                >
                    <Grid item xs={12} md={7} lg={5} xl={5}>
                        <Typography
                            variant="h4"
                            align="left"
                            sx={{
                                fontSize: [
                                    '2rem',
                                    '2rem',
                                    '3rem',
                                    '3.5rem',
                                    '3.5rem',
                                ],
                                fontWeight: '900',
                                lineHeight: [
                                    '2.5rem',
                                    '2.5rem',
                                    '3.5rem',
                                    '4rem',
                                    '4rem',
                                ],
                                color: welcomeTextColor,
                                marginBottom: [
                                    '1rem',
                                    '1rem',
                                    '1rem',
                                    '2rem',
                                    '2rem',
                                ],
                            }}
                        >
                            {surveyState.title}
                        </Typography>
                        <Typography
                            variant="h5"
                            align="left"
                            sx={{
                                fontSize: [
                                    '1.5rem',
                                    '1.5rem',
                                    '2rem',
                                    '2.2rem',
                                    '2.2rem',
                                ],
                                fontWeight: '700',
                                lineHeight: [
                                    '2rem',
                                    '2rem',
                                    '2.5rem',
                                    '3rem',
                                    '3rem',
                                ],
                                color: welcomeTextColor,
                                marginBottom: [
                                    '1rem',
                                    '1rem',
                                    '1.5rem',
                                    '1.5rem',
                                ],
                            }}
                        >
                            Chúc mừng bạn đến với khảo sát
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: welcomeTextColor,
                                fontWeight: '500',
                                fontSize: ['1rem', '1rem', '1.5rem', '1.5rem'],
                                lineHeight: ['1.3rem', '1.5rem', '1.8rem'],
                            }}
                        >
                            Cảm ơn bạn đã đồng ý tham gia. Sẽ chỉ mất vài phút
                            để hoàn thành khảo sát. <br /> Tất cả các câu trả
                            lời của bạn là riêng tư và bí mật.
                        </Typography>
                    </Grid>
                </Grid>
                {surveyState.answerable && questions.length > 0 && (
                    <Grid container spacing={2} marginBottom={'20px'}>
                        <Grid
                            item
                            xs={12}
                            md={12}
                            display={'flex'}
                            alignItems={'center'}
                            gap={2}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                sx={styles.btnStartSurvey}
                                disabled={!locationReady}
                                onClick={handleStartSurvey}
                            >
                                Bắt đầu khảo sát
                            </Button>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: welcomeTextColor,
                                    fontSize: [
                                        '1rem',
                                        '1rem',
                                        '1.5rem',
                                        '1.5rem',
                                    ],
                                    display: [
                                        'none',
                                        'none',
                                        'none',
                                        'flex',
                                        'flex',
                                    ],
                                    alignItems: 'baseline',
                                    gap: 1,
                                }}
                            >
                                Ấn
                                <span
                                    style={{
                                        fontWeight: '700',
                                    }}
                                >
                                    Enter
                                </span>
                                <SubdirectoryArrowLeftIcon
                                    sx={{ fontSize: 32 }}
                                />
                            </Typography>
                        </Grid>
                    </Grid>
                )}
                <Grid container spacing={2}>
                    <Grid
                        item
                        xs={12}
                        md={12}
                        display={'flex'}
                        alignItems={'center'}
                        gap={2}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                color: welcomeTextColor,
                                fontWeight: '400',
                                fontStyle: 'italic',
                                fontSize: ['0.8rem', '1rem', '1rem', '1rem'],
                                lineHeight: ['1.5rem', '1.5rem', '2rem'],
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <AccessTimeIcon
                                sx={{
                                    fontSize: ['1rem', '1.2rem'],
                                }}
                            />
                            Sẽ mất 5 phút để hoàn thành khảo sát này
                        </Typography>
                    </Grid>
                </Grid>
                {openModalShareSurvey && (
                    <ModalShareSurvey
                        open={openModalShareSurvey}
                        onClose={() => setOpenModalShareSurvey(false)}
                    />
                )}
                {openModalCampaignStatus && (
                    <ModalConfirm
                        open={true}
                        onClose={() => setOpenModalCampaignStatus(false)}
                        title="Thông báo"
                        content={
                            <Box
                                sx={{
                                    fontSize: '24px',
                                }}
                            >
                                Chiến dịch khảo sát
                                <b> {`"${surveyState.title}"`}</b> đã kết thúc.
                                <br />
                                Vui lòng kiểm tra lại sau hoặc tham các chiến
                                dịch khác
                            </Box>
                        }
                    />
                )}
                <Dialog open={openStaffIdModal}>
                    <DialogTitle>Nhập mã trả lời</DialogTitle>
                    <DialogContent sx={{ minWidth: '300px' }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Mã"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={staffId}
                            onChange={(e) => setStaffId(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleStaffIdSubmit();
                                }
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenStaffIdModal(false)}>
                            Hủy
                        </Button>
                        <Button
                            onClick={handleStaffIdSubmit}
                            variant="contained"
                        >
                            Xác nhận
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </RequireLocationGate>
    );
};
const styles = createStyleSheet({
    startSurveyLayout: {
        width: '100vw',
        minHeight: '100vh',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: ['2rem', '2rem', '4rem', '4rem'],
    },
    btnStartSurvey: {
        padding: [
            '1rem 3rem',
            '1rem 3rem',
            '1.2rem 3.2rem',
            '1.5rem 3.5rem',
            '1.5rem 1.5rem',
        ],
        minWidth: ['10rem', '10rem', '15rem', '15rem'],
        height: ['2.5rem', '2.7rem', '3rem', '4rem', '4rem'],
        borderRadius: '50px',
        fontSize: ['1rem', '1rem', '1.2rem', '1.5rem', '1.5rem'],
        fontWeight: '700',
        lineHeight: ['1.5rem', '1.5rem', '2rem'],
    },
    btnShare: {
        minWidth: ['5rem', '7rem', '10rem', '12rem', '13rem'],
        height: ['2.5rem', '2rem', '2rem', '3rem', '3rem'],
        textTransform: 'none',
        borderRadius: '40px',
        fontSize: ['0.6rem', '0.7rem', '0.7rem', '1rem', '1rem'],
        fontWeight: '700',
        padding: ['1rem', '1rem', '1rem', '1.5rem 3rem'],
    },
});
export default StartSurvey;
